import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Center, Checkbox, Group, Loader, NumberInput, Select, Stack, Table, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, referenceApi, type PriceIndexInput, type RegionalFactorInput } from '../../api';
import { DESTATIS_SIGNS, DESTATIS_UNSURE, PRICE_INDEX_TYPES, formatQuarter } from '../../format';
import { parseFactors } from './referencePaste';
import { useLazyCount } from '../../ui/PageScroll';

const errorOf = (e: unknown): string => (e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.');
/** Regional factors of the office (AP-80): look up, paste in, remove. */
export function RegionalFactorsTab({ canEdit }: { canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['regional-factors'], queryFn: referenceApi.factors });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['regional-factors'] });
  const [query, setQuery] = useState('');
  const [text, setText] = useState('');
  const [defaults, setDefaults] = useState({ source: 'BKI', edition: '', license: '' });
  const [replace, setReplace] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const add = useMutation({ mutationFn: (rows: RegionalFactorInput[]) => referenceApi.addFactors(rows, replace), onSuccess: () => setText(''), onSettled: refresh });
  const remove = useMutation({ mutationFn: (ids: string[]) => referenceApi.removeFactors(ids), onSuccess: () => setSelected(new Set()), onSettled: refresh });
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = (list.data ?? []).filter((f) => words.every((w) => `${f.area} ${f.district} ${f.source} ${f.edition ?? ''}`.toLowerCase().includes(w)));
  const lazy = useLazyCount(shown.length, 150, query);
  const paste = text.trim() ? parseFactors(text, defaults) : null;
  const readFile = (file: File | undefined): void => { if (file) void file.text().then(setText); };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">Regionalfaktoren bringen einen Kennwert vom Bundesdurchschnitt auf den Kreis des Projekts. Sie stammen aus der eigenen Lizenz (z. B. BKI) und haben dieselbe Freigabe wie der Katalog. Im Kostenplan schlägt der Standort den passenden Kreis vor.</Text>
      {(add.error ?? remove.error) && <Alert color="red" py={6}>{errorOf(add.error ?? remove.error)}</Alert>}
      <Group gap="sm">
        <TextInput size="xs" placeholder="Suchen: Kreis, Bereich, Quelle …" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1 }} />
        <Text size="xs" c="dimmed">{list.data?.length ?? 0} {list.data?.length === 1 ? 'Eintrag' : 'Einträge'}</Text>
        {canEdit && selected.size > 0 && (
          <Button size="xs" color="red" variant="light" loading={remove.isPending} onClick={() => { if (window.confirm(`${selected.size} Regionalfaktoren löschen? Kostenpläne behalten ihren Wert.`)) remove.mutate([...selected]); }}>{selected.size} löschen</Button>
        )}
      </Group>
      {list.isPending && <Center h={60}><Loader size="sm" /></Center>}
      {shown.length > 0 && (
        <Table.ScrollContainer minWidth={600} maxHeight={360}>
          <Table verticalSpacing={2} className="pst-tabular" stickyHeader highlightOnHover>
            <Table.Thead><Table.Tr>{canEdit && <Table.Th w={30} />}<Table.Th w={110}>Bereich</Table.Th><Table.Th>Kreis</Table.Th><Table.Th w={90} ta="right">Faktor</Table.Th><Table.Th w={180}>Quelle</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {shown.slice(0, lazy.shown).map((f) => (
                <Table.Tr key={f.id}>
                  {canEdit && <Table.Td><Checkbox size="xs" aria-label={`${f.district} wählen`} checked={selected.has(f.id)} onChange={() => setSelected((s) => { const n = new Set(s); if (n.has(f.id)) n.delete(f.id); else n.add(f.id); return n; })} /></Table.Td>}
                  <Table.Td><Text size="xs">{f.area}</Text></Table.Td>
                  <Table.Td><Text size="sm">{f.district}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{f.factor.toLocaleString('de-DE', { minimumFractionDigits: 3 })}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{[f.source, f.edition].filter(Boolean).join(' ')}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {lazy.more}
        </Table.ScrollContainer>
      )}
      {canEdit && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>Einfügen</Text>
          <Text size="xs">Spalten <Text span ff="monospace">Bereich;Kreis;Faktor</Text> (z. B. <Text span ff="monospace">Deutschland;Aachen, Städteregion;0,935</Text>), aus Excel kopiert oder als CSV-Datei.</Text>
          <Group gap="xs" align="flex-end">
            <TextInput size="xs" label="Quelle" value={defaults.source} onChange={(e) => { const v = e.currentTarget.value; setDefaults((d) => ({ ...d, source: v })); }} w={100} />
            <TextInput size="xs" label="Ausgabe/Jahr" placeholder="z. B. 2025" value={defaults.edition} onChange={(e) => { const v = e.currentTarget.value; setDefaults((d) => ({ ...d, edition: v })); }} w={120} />
            <TextInput size="xs" label="Lizenzkennzeichnung" value={defaults.license} onChange={(e) => { const v = e.currentTarget.value; setDefaults((d) => ({ ...d, license: v })); }} w={200} />
          </Group>
          <Textarea autosize minRows={4} maxRows={12} value={text} onChange={(e) => setText(e.currentTarget.value)} styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} />
          <Group gap="xs">
            <Button size="xs" variant="default" component="label">CSV-Datei wählen<input type="file" accept=".csv,.txt,text/csv,text/plain" hidden onChange={(e) => readFile(e.currentTarget.files?.[0])} /></Button>
            <Checkbox size="xs" label="frühere Einträge derselben Quelle und Ausgabe ersetzen" checked={replace} onChange={(e) => setReplace(e.currentTarget.checked)} />
            {paste && <Text size="sm">{paste.rows.length} lesbar{paste.bad.length ? `, nicht lesbar: Zeile ${paste.bad.slice(0, 8).join(', ')}` : ''}</Text>}
            <Button size="xs" disabled={!paste || paste.rows.length === 0} loading={add.isPending} onClick={() => paste && add.mutate(paste.rows)}>{paste?.rows.length ? `${paste.rows.length} übernehmen` : 'Übernehmen'}</Button>
          </Group>
          {add.isSuccess && <Text size="sm" c="green.7">{add.data.added} Regionalfaktoren übernommen.</Text>}
        </Stack>
      )}
    </Stack>
  );
}

type DestatisOutcome = { file: string; table?: string; saved?: number; periods?: string[]; declined?: string | null; error?: string };

/**
 * Building price index values (AP-80): read from the Destatis CSV download
 * (GENESIS-Online, statistic 61261 – the API delivers nothing for it, AP-83)
 * or entered by hand. The conversion of catalog values uses the total line
 * ("insgesamt") without VAT; the trades are kept for looking up.
 */
export function PriceIndexTab({ canEdit }: { canEdit: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['price-indices'], queryFn: referenceApi.indices });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['price-indices'] });
  const [row, setRow] = useState<{ buildingType: string; period: string; value: number | ''; base: string; source: string }>({ buildingType: 'Wohngebäude', period: '', value: '', base: '2021 = 100', source: 'Destatis, Tabelle 61261-0002' });
  const save = useMutation({ mutationFn: (rows: PriceIndexInput[]) => referenceApi.saveIndices(rows), onSuccess: () => setRow((r) => ({ ...r, period: '', value: '' })), onSettled: refresh });
  const remove = useMutation({ mutationFn: (id: string) => referenceApi.removeIndices([id]), onSettled: refresh });
  const [outcomes, setOutcomes] = useState<DestatisOutcome[]>([]);
  const [reading, setReading] = useState(false);
  const all = list.data ?? [];
  const seriesList = [...new Set([...PRICE_INDEX_TYPES, ...all.map((i) => i.buildingType)])];
  const [series, setSeries] = useState<string>('Wohngebäude');
  const [work, setWork] = useState<string>('insgesamt');
  const [vat, setVat] = useState<'ohne' | 'mit'>('ohne');
  const works = [...new Set(all.filter((i) => i.buildingType === series).map((i) => i.work))].sort((a, b) => (a === 'insgesamt' ? -1 : b === 'insgesamt' ? 1 : a.localeCompare(b)));
  const shown = all.filter((i) => i.buildingType === series && i.work === work && i.vat === vat).sort((a, b) => b.period.localeCompare(a.period));
  const lazy = useLazyCount(shown.length, 150, `${series}|${work}|${vat}`);
  const validPeriod = /^\d{4}(-Q[1-4])?$/.test(row.period);

  const genesis = useQuery({ queryKey: ['genesis-status'], queryFn: referenceApi.genesisStatus });
  const fetchNow = useMutation({
    mutationFn: referenceApi.fetchGenesis,
    onSuccess: (r) => setOutcomes(r.map((x) => ({ file: `${x.table} (GENESIS-Online)`, table: x.table, title: '', saved: x.saved, periods: x.periods, declined: null, ...(x.error ? { error: x.error } : {}) }))),
    onSettled: refresh,
  });
  const readFiles = async (files: FileList | null): Promise<void> => {
    if (!files?.length) return;
    setReading(true);
    const out: DestatisOutcome[] = [];
    for (const f of Array.from(files)) {
      try {
        const r = await referenceApi.importDestatis(f.name, await f.text());
        out.push({ file: f.name, ...r });
      } catch (e) {
        out.push({ file: f.name, error: errorOf(e) });
      }
    }
    setOutcomes(out);
    setReading(false);
    await refresh();
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Baupreisindex von Destatis (Statistik 61261, 2021 = 100). Er rechnet den Preisstand eines Kennwerts auf heute um: Kennwert × BPI_aktuell / BPI_Preisstand. PST verwendet dafür die Gesamtzeile ohne Umsatzsteuer der gewählten Gebäudeart, für die Kostengruppe 500 die Außenanlagen; fehlt ein Quartal, den Jahreswert. Die Einzelgewerke bleiben zum Nachschlagen.
      </Text>
      {(save.error ?? remove.error) && <Alert color="red" py={6}>{errorOf(save.error ?? remove.error)}</Alert>}
      {canEdit && (
        <Stack gap={4}>
          {genesis.data?.configured && (
            <Group gap="xs">
              <Button size="xs" onClick={() => fetchNow.mutate()} loading={fetchNow.isPending}>Von Destatis abrufen</Button>
              <Text size="xs" c="dimmed">Holt die sechs Tabellen ab 2015 über die Schnittstelle GENESIS-Online; PST tut das auch einmal am Tag von selbst.</Text>
            </Group>
          )}
          {fetchNow.isError && <Alert color="red" py={6}>{errorOf(fetchNow.error)}</Alert>}
          <Group gap="xs">
            <Button size="xs" variant="default" component="label" loading={reading}>
              Destatis-Dateien einlesen …
              <input type="file" accept=".csv,text/csv" multiple hidden onChange={(e) => void readFiles(e.currentTarget.files)} />
            </Button>
            <Text size="xs" c="dimmed">CSV aus GENESIS-Online, z. B. 61261-0001 (Jahre) und 61261-0002 (Quartale); mehrere auf einmal. Beim Herunterladen den Zeitraum ab 2021 wählen, sonst liefert Destatis nur das letzte Jahr.</Text>
          </Group>
          {outcomes.length > 0 && (
            <Table verticalSpacing={2}>
              <Table.Tbody>
                {outcomes.map((o) => (
                  <Table.Tr key={o.file}>
                    <Table.Td w={220}><Text size="xs" ff="monospace">{o.file}</Text></Table.Td>
                    <Table.Td>
                      <Text size="xs" c={o.error ? 'red' : o.declined ? 'dimmed' : undefined}>
                        {o.error ?? (o.declined ? `nicht übernommen: ${o.declined}` : `${o.saved} Werte übernommen, ${formatQuarter(o.periods![0]!)} bis ${formatQuarter(o.periods![1]!)}`)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Select size="xs" label="Reihe" data={seriesList} value={series} onChange={(v) => { if (v) { setSeries(v); setWork('insgesamt'); } }} allowDeselect={false} w={300} comboboxProps={{ withinPortal: true }} />
        <Select size="xs" label="Bauarbeiten" data={works.length ? works : ['insgesamt']} value={work} onChange={(v) => v && setWork(v)} allowDeselect={false} searchable w={300} comboboxProps={{ withinPortal: true }} />
        <Select size="xs" label="Umsatzsteuer" data={[{ value: 'ohne', label: 'ohne' }, { value: 'mit', label: 'einschließlich' }]} value={vat} onChange={(v) => v && setVat(v as 'ohne' | 'mit')} allowDeselect={false} w={150} comboboxProps={{ withinPortal: true }} />
        <Text size="xs" c="dimmed">{all.length} Werte gespeichert</Text>
      </Group>
      {list.isPending && <Center h={60}><Loader size="sm" /></Center>}
      {list.data && shown.length === 0 && <Text size="sm" c="dimmed">Für diese Auswahl noch keine Werte.</Text>}
      {shown.length > 0 && (
        <Table.ScrollContainer minWidth={600} maxHeight={360}>
          <Table verticalSpacing={2} className="pst-tabular" highlightOnHover stickyHeader>
            <Table.Thead><Table.Tr><Table.Th w={160}>Zeitraum</Table.Th><Table.Th w={90} ta="right">Wert</Table.Th><Table.Th w={60}>Zeichen</Table.Th><Table.Th w={110}>Basis</Table.Th><Table.Th>Quelle</Table.Th>{canEdit && <Table.Th w={30} />}</Table.Tr></Table.Thead>
            <Table.Tbody>
              {shown.slice(0, lazy.shown).map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td><Text size="sm">{formatQuarter(i.period)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{i.value.toLocaleString('de-DE', { minimumFractionDigits: 1 })}</Text></Table.Td>
                  <Table.Td><Text size="xs" c={(i.flag ?? '').split(' ').some((f) => DESTATIS_UNSURE[f]) ? 'orange.7' : 'dimmed'} title={(i.flag ?? '').split(' ').map((f) => DESTATIS_SIGNS[f]).filter(Boolean).join('; ') || undefined}>{i.flag}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{i.base}</Text></Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{i.source}</Text></Table.Td>
                  {canEdit && <Table.Td><Button size="compact-xs" variant="subtle" color="gray" aria-label="Indexwert löschen" onClick={() => remove.mutate(i.id)}>✕</Button></Table.Td>}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          {lazy.more}
        </Table.ScrollContainer>
      )}
      <details>
        <summary><Text span size="xs" c="dimmed">Zeichenerklärung (Destatis)</Text></summary>
        <Table verticalSpacing={0} mt={4}>
          <Table.Tbody>
            {Object.entries(DESTATIS_SIGNS).map(([sign, text]) => (
              <Table.Tr key={sign}><Table.Td w={50}><Text size="xs" ff="monospace">{sign}</Text></Table.Td><Table.Td><Text size="xs">{text}</Text></Table.Td></Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text size="xs" c="dimmed" mt={4}>Zellen ohne Zahl („-“, „.“, „...“, „/“, „x“) übernimmt PST nicht; Werte in Klammern mit dem Zeichen „()“. Vorläufige, geschätzte oder eingeschränkte Werte nennt PST in der Quelle jeder Position, die sie verwendet.</Text>
      </details>
      {canEdit && (
        <Group gap="xs" align="flex-end" wrap="wrap">
          <Select size="xs" label="Von Hand: Reihe" data={seriesList} value={row.buildingType} onChange={(v) => v && setRow((r) => ({ ...r, buildingType: v }))} allowDeselect={false} w={240} comboboxProps={{ withinPortal: true }} />
          <TextInput size="xs" label="Quartal oder Jahr" placeholder="2026-Q2" value={row.period} onChange={(e) => { const v = e.currentTarget.value.trim().toUpperCase(); setRow((r) => ({ ...r, period: v })); }} error={row.period && !validPeriod ? 'JJJJ-Qn oder JJJJ' : undefined} w={130} />
          <NumberInput size="xs" label="Wert" decimalSeparator="," min={1} max={10000} decimalScale={2} value={row.value} onChange={(v) => setRow((r) => ({ ...r, value: typeof v === 'number' ? v : '' }))} w={100} hideControls />
          <TextInput size="xs" label="Basis" value={row.base} onChange={(e) => { const v = e.currentTarget.value; setRow((r) => ({ ...r, base: v })); }} w={110} />
          <TextInput size="xs" label="Quelle" value={row.source} onChange={(e) => { const v = e.currentTarget.value; setRow((r) => ({ ...r, source: v })); }} w={230} />
          <Button size="xs" disabled={!validPeriod || row.value === ''} loading={save.isPending}
            onClick={() => row.value !== '' && save.mutate([{ buildingType: row.buildingType, period: row.period, value: row.value, base: row.base.trim() || null, source: row.source.trim() || null }])}>Speichern</Button>
          <Text size="xs" c="dimmed">gilt als Gesamtzeile ohne Umsatzsteuer</Text>
        </Group>
      )}
    </Stack>
  );
}
